import { GraphQLError } from 'graphql/index';
import { useRouter } from 'next/router';
import React from 'react';
import { graphql } from 'react-relay';

import Card from '../../../components/core/Card';
import DataTable, { RowWrapper } from '../../../components/core/DataTable';
import DescriptionList from '../../../components/core/DescriptionList';
import Dialog from '../../../components/core/Dialog';
import DialogButton from '../../../components/core/DialogButton';
import DialogHandler from '../../../components/core/DialogHandler';
import Grid from '../../../components/core/Grid';
import Head from '../../../components/core/Head';
import HorizontalDivider from '../../../components/core/HorizontalDivider';
import Image from '../../../components/core/Image';
import Label from '../../../components/core/Label';
import { HeaderSidebarNavPageLayout } from '../../../components/core/Layout';
import MutationConfirmButton from '../../../components/core/MutationConfirmButton';
import Stack from '../../../components/core/Stack';
import Text from '../../../components/core/Text';
import View from '../../../components/core/View';
import ProductDiscountUpdateDialog from '../../../components/productDiscount/ProductDiscountUpdateDialog';
import PromotionActionsMutationButtonStack from '../../../components/promotion/PromotionActionsMutationButtonStack';
import PromotionDescriptionList from '../../../components/promotion/PromotionDescriptionList';
import PromotionAppBannerPreview from '../../../components/promotion/PromotionPageAppBannerPreview';
import PromotionAppMiniBannerPreview from '../../../components/promotion/PromotionPageAppMiniBannerPreview';
import PromotionPageUpsertDialog from '../../../components/promotion/PromotionPageUpsertDialog';
import PromotionProblemEventDescriptionList from '../../../components/promotion/PromotionProblemEventDescriptionList';
import PromotionProblemEventUpsertDialog from '../../../components/promotion/PromotionProblemEventUpsertDialog';
import PromotionProductEventDescriptionList from '../../../components/promotion/PromotionProductEventDescriptionList';
import PromotionProductEventUpsertDialog from '../../../components/promotion/PromotionProductEventUpsertDialog';
import useLazyLoadQuery from '../../../hooks/useLazyLoadQuery';
import useToast from '../../../hooks/useToast';
import { PromotionId_promotionPageDeleteMutation } from '../../../relay/__generated__/PromotionId_promotionPageDeleteMutation.graphql';
import { PromotionId_promotionProblemEventDeleteMutation } from '../../../relay/__generated__/PromotionId_promotionProblemEventDeleteMutation.graphql';
import { PromotionId_promotionProductEventDeleteMutation } from '../../../relay/__generated__/PromotionId_promotionProductEventDeleteMutation.graphql';
import { PromotionId_promotionQuery } from '../../../relay/__generated__/PromotionId_promotionQuery.graphql';
import { parseGraphQLError } from '../../../utils/error';
import { numberWithCommas } from '../../../utils/number';
import { NextPage } from '../../_app';

const promotionForPromotionId = graphql`
  query PromotionId_promotionQuery($id: ID!) {
    promotion(id: $id) {
      id
      title
      actions
      ...PromotionActionsMutationButtonStack_promotion

      ...PromotionDescriptionList_promotion

      ...PromotionProblemEventDescriptionList_promotion
      ...PromotionProblemEventUpsertDialog_promotion
      problemEvent {
        __typename
      }

      ...PromotionProductEventDescriptionList_promotion
      ...PromotionProductEventUpsertDialog_promotion
      productEvent {
        discounts {
          id
          webImage {
            id
          }
          mobileImage {
            id
          }
          amount
          product {
            title
          }
          ...ProductDiscountUpdateDialog_productDiscount
        }
      }

      ...PromotionPageUpsertDialog_promotion
      page {
        __typename

        bannerBackgroundColor
        bannerImage {
          objectUrl
        }
        bannerSubText
        bannerSubTextColor
        bannerTitle
        bannerTitleColor
        miniBannerSubText
        miniBannerTitle

        detailBackgroundColor
        detailImages {
          image {
            objectUrl
          }
        }
        detailButtonText
      }
    }
  }
`;

type Props = {};

const PromotionId: NextPage<Props> = () => {
  const router = useRouter();
  const { toast } = useToast();

  const [{ promotion }, refresh] = useLazyLoadQuery<PromotionId_promotionQuery>(promotionForPromotionId, {
    id: router.query.promotionId as string,
  });
  if (!promotion) return null;
  const { id, title, actions, productEvent, problemEvent, page } = promotion;

  const canUpdateContent = actions.includes('promotion_update');

  return (
    <View>
      <Head siteTitle={`프로모션 - ${title}`} />
      <View>
        <Grid sx={{ alignItems: 'center' }}>
          <Grid.Unit size={'max'}>
            <Text as={'h1'}>{title}</Text>
          </Grid.Unit>
          <Grid.Unit size={'min'}>
            <PromotionActionsMutationButtonStack promotion={promotion} />
          </Grid.Unit>
        </Grid>
        <HorizontalDivider mt={[3, 3, 0]} mb={5} />
        <Grid reverse={[true, true, false]} gapX={5} gapY={3}>
          <Grid.Unit size={[1, 1, 3 / 4]}>
            {/* region 프로모션 정보 */}
            <Text fontSize={3} fontWeight={'bold'}>
              프로모션 정보
            </Text>
            <Card sx={{ padding: 4, marginTop: 2 }}>
              <PromotionDescriptionList
                promotion={promotion}
                type={'default'}
                titleUnitSize={1 / 4}
                descriptionUnitSize={3 / 4}
              />
            </Card>
            {/* endregion */}

            {/* region 문제 이벤트 */}
            <View sx={{ marginTop: 5 }}>
              <Grid wrap={false} sx={{ alignItems: 'center' }} gapX={2}>
                <Grid.Unit size={'max'}>
                  <Text fontSize={3} fontWeight={'bold'}>
                    문제 이벤트
                  </Text>
                </Grid.Unit>
                {canUpdateContent ? (
                  <>
                    <Grid.Unit size={'min'}>
                      <DialogButton
                        size={'large'}
                        variant={'outline'}
                        renderDialog={({ isOpen, closeDialog }) => (
                          <PromotionProblemEventUpsertDialog
                            wide
                            promotion={promotion}
                            isOpen={isOpen}
                            onDismiss={closeDialog}
                            config={{
                              onCompleted: () => {
                                toast(`문제 이벤트 정보를 ${problemEvent ? '수정' : '입력'}했어요`, 'success');
                                refresh();
                                closeDialog();
                              },
                              onError: () => {
                                toast('다시 시도해주세요', 'error');
                              },
                            }}
                          />
                        )}
                      >
                        {problemEvent ? '수정하기' : '입력하기'}
                      </DialogButton>
                    </Grid.Unit>
                    {problemEvent ? (
                      <Grid.Unit size={'min'}>
                        <MutationConfirmButton<PromotionId_promotionProblemEventDeleteMutation>
                          mutation={graphql`
                            mutation PromotionId_promotionProblemEventDeleteMutation(
                              $input: PromotionProblemEventDeleteInput!
                            ) {
                              promotionProblemEventDelete(input: $input) {
                                __typename
                              }
                            }
                          `}
                          input={{ promotionId: id }}
                          variant={'danger'}
                          message={'입력된 정보를 모두 초기화할까요?'}
                          config={{
                            onCompleted: () => {
                              toast('문제 이벤트 초기화가 완료됐어요!', 'success');
                              refresh();
                            },
                            onError: (error) => {
                              toast(
                                parseGraphQLError(error as GraphQLError)?.[0].message || '다시 시도해주세요',
                                'error',
                              );
                            },
                          }}
                          size={'large'}
                        >
                          초기화하기
                        </MutationConfirmButton>
                      </Grid.Unit>
                    ) : null}
                  </>
                ) : null}
              </Grid>
              <Card sx={{ padding: 4, marginTop: 2 }}>
                <PromotionProblemEventDescriptionList
                  promotion={promotion}
                  titleUnitSize={1 / 4}
                  descriptionUnitSize={3 / 4}
                />
              </Card>
            </View>
            {/* endregion */}

            {/* region 할인 이벤트 */}
            <View sx={{ marginTop: 5 }}>
              <Grid wrap={false} sx={{ alignItems: 'center' }} gapX={2}>
                <Grid.Unit size={'max'}>
                  <Text fontSize={3} fontWeight={'bold'}>
                    할인 이벤트
                  </Text>
                </Grid.Unit>
                {canUpdateContent ? (
                  <>
                    <Grid.Unit size={'min'}>
                      <DialogButton
                        size={'large'}
                        variant={'outline'}
                        renderDialog={({ isOpen, closeDialog }) => (
                          <PromotionProductEventUpsertDialog
                            wide
                            promotion={promotion}
                            isOpen={isOpen}
                            onDismiss={closeDialog}
                            config={{
                              onCompleted: () => {
                                toast(`할인 이벤트 정보를 ${productEvent ? '수정' : '입력'}했어요`, 'success');
                                refresh();
                                closeDialog();
                              },
                              onError: () => {
                                toast('다시 시도해주세요', 'error');
                              },
                            }}
                          />
                        )}
                      >
                        {productEvent ? '수정하기' : '입력하기'}
                      </DialogButton>
                    </Grid.Unit>
                    {productEvent ? (
                      <Grid.Unit size={'min'}>
                        <MutationConfirmButton<PromotionId_promotionProductEventDeleteMutation>
                          mutation={graphql`
                            mutation PromotionId_promotionProductEventDeleteMutation(
                              $input: PromotionProductEventDeleteInput!
                            ) {
                              promotionProductEventDelete(input: $input) {
                                __typename
                              }
                            }
                          `}
                          input={{ promotionId: id }}
                          variant={'danger'}
                          message={'입력된 정보를 모두 초기화할까요?'}
                          config={{
                            onCompleted: () => {
                              toast('할인 이벤트 초기화가 완료됐어요!', 'success');
                              refresh();
                            },
                            onError: (error) => {
                              toast(
                                parseGraphQLError(error as GraphQLError)?.[0].message || '다시 시도해주세요',
                                'error',
                              );
                            },
                          }}
                          size={'large'}
                        >
                          초기화하기
                        </MutationConfirmButton>
                      </Grid.Unit>
                    ) : null}
                  </>
                ) : null}
              </Grid>
              <Card sx={{ padding: 4, marginTop: 2 }}>
                <PromotionProductEventDescriptionList
                  promotion={promotion}
                  titleUnitSize={1 / 4}
                  descriptionUnitSize={3 / 4}
                />
              </Card>
              {productEvent ? (
                <>
                  <View sx={{ marginTop: 4 }}>
                    <Stack gapX={'12px'}>
                      <Stack.Item>
                        <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                          총 {numberWithCommas(productEvent?.discounts.length || 0)}개
                        </Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text sx={{ fontSize: 0, color: 'fg.subtle', ml: '12px' }}>
                          이미지를 모두 등록해야 이벤트를 활성화할 수 있어요
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </View>
                  <View sx={{ marginTop: 2, overflowX: 'auto' }}>
                    <DataTable
                      columns={[
                        {
                          field: 'product.status',
                          title: '상태',
                          width: 80,
                          renderValue: ({ webImage, mobileImage }) =>
                            webImage && mobileImage ? (
                              <Label variant={'secondary'}>완료</Label>
                            ) : (
                              <Label variant={'attention'}>등록 전</Label>
                            ),
                        },
                        {
                          field: 'product.title',
                          title: '상품명',
                          width: 512,
                          renderValue: ({ product }) => <Text fontSize={1}>{product.title}</Text>,
                        },
                        {
                          field: 'amount',
                          title: '할인 금액',
                          width: 216,
                          renderValue: ({ amount }) => <Text fontSize={1}>{`${numberWithCommas(amount)}원 할인`}</Text>,
                        },
                      ]}
                      rows={productEvent.discounts}
                      renderRowWrapper={(children, productDiscount) => {
                        const { id } = productDiscount;
                        return (
                          <RowWrapper
                            key={id}
                            sx={
                              canUpdateContent
                                ? {
                                    'cursor': 'pointer',
                                    ':hover': {
                                      backgroundColor: 'canvas.inset',
                                      transition: 'background-color 250ms',
                                    },
                                  }
                                : { pointerEvents: 'none' }
                            }
                          >
                            <DialogHandler
                              renderDialog={({ isOpen, closeDialog }) => (
                                <ProductDiscountUpdateDialog
                                  productDiscount={productDiscount}
                                  isOpen={isOpen}
                                  onDismiss={closeDialog}
                                  full
                                  config={{
                                    onCompleted: () => {
                                      toast('이미지 업로드가 완료됐어요!', 'success');
                                      closeDialog();
                                      refresh();
                                    },
                                    onError: () => {
                                      toast('다시 업로드해 주세요', 'error');
                                    },
                                  }}
                                />
                              )}
                            >
                              {children}
                            </DialogHandler>
                          </RowWrapper>
                        );
                      }}
                    />
                  </View>
                </>
              ) : null}
            </View>
            {/* endregion */}

            {/* region 프로모션 페이지 */}
            <View sx={{ marginTop: 5 }}>
              <Grid wrap={false} sx={{ alignItems: 'center' }} gapX={2}>
                <Grid.Unit size={'max'}>
                  <Text fontSize={3} fontWeight={'bold'}>
                    프로모션 페이지
                  </Text>
                </Grid.Unit>
                {canUpdateContent ? (
                  <>
                    <Grid.Unit size={'min'}>
                      <DialogButton
                        size={'large'}
                        variant={'outline'}
                        renderDialog={({ isOpen, closeDialog }) => (
                          <PromotionPageUpsertDialog
                            wide
                            promotion={promotion}
                            isOpen={isOpen}
                            onDismiss={closeDialog}
                            config={{
                              onCompleted: () => {
                                toast(`프로모션 페이지를 ${page ? '수정' : '입력'}했어요`, 'success');
                                refresh();
                                closeDialog();
                              },
                              onError: () => {
                                toast('다시 시도해주세요', 'error');
                              },
                            }}
                          />
                        )}
                      >
                        {page ? '수정하기' : '입력하기'}
                      </DialogButton>
                    </Grid.Unit>
                    {page ? (
                      <Grid.Unit size={'min'}>
                        <MutationConfirmButton<PromotionId_promotionPageDeleteMutation>
                          mutation={graphql`
                            mutation PromotionId_promotionPageDeleteMutation($input: PromotionPageDeleteInput!) {
                              promotionPageDelete(input: $input) {
                                __typename
                              }
                            }
                          `}
                          input={{ promotionId: id }}
                          variant={'danger'}
                          message={'입력된 정보를 모두 초기화할까요?'}
                          config={{
                            onCompleted: () => {
                              toast('프로모션 페이지 초기화가 완료됐어요!', 'success');
                              refresh();
                            },
                            onError: (error) => {
                              toast(
                                parseGraphQLError(error as GraphQLError)?.[0].message || '다시 시도해주세요',
                                'error',
                              );
                            },
                          }}
                          size={'large'}
                        >
                          초기화하기
                        </MutationConfirmButton>
                      </Grid.Unit>
                    ) : null}
                  </>
                ) : null}
              </Grid>
              <Card sx={{ padding: 4, marginTop: 2 }}>
                <DescriptionList
                  item={promotion}
                  itemDescriptions={{
                    image: {
                      title: '이미지',
                      renderValue: ({ page }) => (
                        <>
                          {page ? (
                            <>
                              <Text>완료</Text>
                              <DialogHandler
                                renderDialog={({ isOpen, closeDialog }) => (
                                  <Dialog
                                    isOpen={isOpen}
                                    onDismiss={closeDialog}
                                    sx={{ backgroundColor: page.detailBackgroundColor, pt: 6, pb: 8 }}
                                  >
                                    <Dialog.Body>
                                      {page.detailImages?.map(({ image: { objectUrl } }, index) => (
                                        <Image
                                          key={objectUrl + index}
                                          src={objectUrl}
                                          alt={objectUrl}
                                          html
                                          style={{ display: 'block', maxWidth: 420, margin: '0 auto' }}
                                        />
                                      ))}
                                      <View
                                        px={5}
                                        pb={4}
                                        pt={6}
                                        position={'absolute'}
                                        bottom={0}
                                        left={0}
                                        right={0}
                                        background={`linear-gradient(180deg, ${page.detailBackgroundColor + '00'} 0%, ${
                                          page.detailBackgroundColor
                                        } 29.69%, ${page.detailBackgroundColor} 100%)`}
                                      >
                                        <View borderRadius={999} backgroundColor={'#835EEB'} textAlign={'center'} p={2}>
                                          <Text color={'white'} fontSize={1}>
                                            {page.detailButtonText}
                                          </Text>
                                        </View>
                                      </View>
                                    </Dialog.Body>
                                  </Dialog>
                                )}
                              >
                                <View>
                                  <View width={235} mt={2} sx={{ cursor: 'pointer' }}>
                                    <PromotionAppBannerPreview
                                      title={page.bannerTitle}
                                      subText={page.bannerSubText}
                                      backgroundColor={page.bannerBackgroundColor}
                                      titleColor={page.bannerTitleColor}
                                      subTextColor={page.bannerSubTextColor}
                                      imageUrl={page.bannerImage?.objectUrl}
                                    />
                                  </View>
                                  <View width={262} mt={2} sx={{ cursor: 'pointer' }}>
                                    <PromotionAppMiniBannerPreview
                                      title={page.miniBannerTitle}
                                      subText={page.miniBannerSubText}
                                      imageUrl={page.bannerImage?.objectUrl}
                                    />
                                  </View>
                                </View>
                              </DialogHandler>
                            </>
                          ) : (
                            '등록 전'
                          )}
                        </>
                      ),
                    },
                  }}
                  picks={['image']}
                  titleUnitSize={1 / 4}
                  descriptionUnitSize={3 / 4}
                />
              </Card>
            </View>
            {/* endregion */}
          </Grid.Unit>
          <Grid.Unit size={[1, 1, 1 / 4]}>
            <PromotionDescriptionList
              promotion={promotion}
              type={'activity'}
              titleUnitSize={'max'}
              descriptionUnitSize={'min'}
              renderTitle={(title) => <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>{title}</Text>}
            />
          </Grid.Unit>
        </Grid>
      </View>
    </View>
  );
};

PromotionId.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
PromotionId.authenticated = true;
PromotionId.routes = [
  {
    id: 'promotionId',
    name: '프로모션 상세',
    pathname: '/promotion/[promotionId]',
    permissions: ['promotion_read'],
  },
];

export default PromotionId;
